/**
 * Wizard Service — the Vesta-direct application surface (`/api/v1/application/*`).
 *
 * All routes take the PATH loan id (`pathLoanId` from NewLoanResponse) — NOT
 * the Vesta guid (`LoanResponse.id`). Mixing them yields 404s that look like
 * auth bugs (spec §8.7).
 *
 * 409 Conflict = Vesta-side failure (originator not found, Vesta validation,
 * outage). Body is `{ error: "..." }` (parsed by api-client parseErrorBody);
 * not retryable — surface the message and keep the form dirty.
 */

import { apiClient } from './api-client';
import type {
  DownloadWizardDocumentRequest,
  DownloadWizardDocumentResponse,
  GetDocumentAssociationOptionsResponse,
  GetWizardDocumentsResponse,
  LoanOfficerLicense,
  LoanResponse,
  NewLoanRequest,
  NewLoanResponse,
  SubmitWizardRequest,
  SubmitWizardResponse,
  UpdateApplicationResponse,
  UpdateLoanRequest,
  UploadWizardDocumentItem,
  UploadWizardDocumentsResponse,
} from '@/types/wizard';

const BASE = '/api/v1/application';

/** Request options shared by wizard saves: caller owns error UX, no blind retries. */
const SAVE_OPTS = { suppressToast: true, retries: 0 } as const;

export const wizardService = {
  /**
   * Create the loan **in Vesta immediately**. Call EXACTLY ONCE per
   * application — use `useCreateApplicationOnce()` which enforces the
   * create-once guard; never call this directly from step code.
   */
  create(body: NewLoanRequest): Promise<NewLoanResponse> {
    return apiClient.post<NewLoanResponse>(BASE, body, SAVE_OPTS);
  },

  /** Live Vesta-derived read (borrower/coBorrower/assets/property + loanNumber). */
  get(loanId: string): Promise<LoanResponse> {
    return apiClient.get<LoanResponse>(`${BASE}/${loanId}`, SAVE_OPTS);
  },

  /**
   * Set-vs-unset DELTA patch (spec §3). Build bodies with `buildPatchPayload`
   * from RHF dirtyFields — never serialize whole form state.
   */
  update(loanId: string, delta: UpdateLoanRequest): Promise<UpdateApplicationResponse> {
    return apiClient.patch<UpdateApplicationResponse>(`${BASE}/${loanId}`, delta, SAVE_OPTS);
  },

  /** One submit; body is a final delta (same shape as PATCH). */
  submit(loanId: string, delta: SubmitWizardRequest): Promise<SubmitWizardResponse> {
    return apiClient.post<SubmitWizardResponse>(`${BASE}/${loanId}/submit`, delta, SAVE_OPTS);
  },

  documents: {
    list(loanId: string): Promise<GetWizardDocumentsResponse> {
      return apiClient.get<GetWizardDocumentsResponse>(`${BASE}/${loanId}/documents`);
    },

    /** JSON base64 upload (NOT multipart). Check per-item `results[].error`. */
    upload(
      loanId: string,
      items: UploadWizardDocumentItem[],
      opts?: { signal?: AbortSignal },
    ): Promise<UploadWizardDocumentsResponse> {
      return apiClient.post<UploadWizardDocumentsResponse>(`${BASE}/${loanId}/documents/upload`, items, opts);
    },

    /** NP-488: the valid "Associated to" entities for this type on this loan. */
    associationOptions(
      loanId: string,
      documentType: string,
    ): Promise<GetDocumentAssociationOptionsResponse> {
      return apiClient.get<GetDocumentAssociationOptionsResponse>(
        `${BASE}/${loanId}/documents/association-options?documentType=${encodeURIComponent(documentType)}`,
        // Review (Copilot): an optional lookup. It 404s until afn-path-api#90 is
        // deployed, and the borrower should see the old upload flow, not a toast and
        // three retries. Same treatment as useDocumentTypes.
        { retries: 0, suppressToast: true },
      );
    },

    /** POST returning base64 JSON — convert to Blob/object URL client-side. */
    download(loanId: string, body: DownloadWizardDocumentRequest): Promise<DownloadWizardDocumentResponse> {
      return apiClient.post<DownloadWizardDocumentResponse>(`${BASE}/${loanId}/documents/download`, body);
    },
  },

  /** LO state licenses straight from Vesta (`stateLicensingInfos`). */
  loLicenses(email: string): Promise<LoanOfficerLicense[]> {
    return apiClient.get<LoanOfficerLicense[]>(
      `${BASE}/loan-officers/${encodeURIComponent(email)}/licenses`,
    );
  },
};

// ─── TanStack Query keys (spec §5.3) ─────────────────────────────────────────
//
// Invalidation strategy:
//  - PATCH/submit responses carry the fresh LoanResponse → write back with
//    `queryClient.setQueryData(applicationKeys.detail(loanId), resp)` in
//    onSuccess instead of invalidate-and-refetch (every GET is a live Vesta
//    round-trip; keep staleTime ≥ 60s).
//  - Create: setQueryData(detail(pathLoanId)) + invalidate incomplete list.
//  - Documents upload onSuccess → invalidate documents(loanId).
//  - Servicing reads keep their unchanged `['loan', loanId, ...]` keys.

export const applicationKeys = {
  all: ['application'] as const,
  detail: (loanId: string) => ['application', loanId] as const,
  documents: (loanId: string) => ['application', loanId, 'documents'] as const,
  // The list `useIncompleteApplications` serves, which is what the loan switcher
  // and LoanContext's auto-select read. It is spelled under `loans` rather than
  // `application` because that hook owns it — and this key must BE that query's
  // key, not merely resemble it: as `['applications', 'incomplete']` the create's
  // invalidation below hit a key nothing subscribed to, so a newly created loan
  // stayed absent from the switcher for the list's full 60s staleTime.
  incomplete: ['loans', 'incomplete'] as const,
  loLicenses: (email: string) => ['lo-licenses', email.toLowerCase()] as const,
};
